import { Controller } from "@hotwired/stimulus";
import { whenConsentGranted } from "../lib/cookiebot_consent";

const DEFAULT_SCRIPT_URL = "https://mapixl.com/scripts/pixlv5.min.js";
const SCRIPT_ID = "abbot-script";
const NOOP_HANDLER_NAMES = [
  "IOS_BRIDGE_NO_REPLY",
  "navigationPerformanceLoggerWithReply",
];

export default class extends Controller {
  static values = {
    userId: { type: String, default: "unknown" },
    pixelId: { type: String, default: "74" },
    key: { type: String, default: "Dogs2026" },
    environment: { type: String, default: "prod" },
    session: { type: String, default: "30" },
    scriptUrl: { type: String, default: DEFAULT_SCRIPT_URL },
  };

  connect() {
    this.installNativeBridgeFallback();

    this.consentSubscription = whenConsentGranted("marketing", () => {
      this.ensureScriptLoaded()
        .then(() => this.onPixelReady())
        .catch((error) => {
          console.error("[MaPixel] Failed to load script", error);
        });
    });
  }

  disconnect() {
    this.consentSubscription?.();
    this.consentSubscription = null;
  }

  onPixelReady() {
    if (typeof window.maTrackEvent !== "function") {
      console.error("[MaPixel] maTrackEvent is not defined after script load");
      return;
    }

    this.installReadyQueue();
    this.trackPageView();
  }

  installReadyQueue() {
    const queued = (window.maq && window.maq.q) || [];

    window.maq = function () {
      window.maTrackEvent.apply(window, arguments);
    };

    queued.forEach((args) => window.maq.apply(window, args));
  }

  installNativeBridgeFallback() {
    // Real iOS WKWebViews (in-app browsers like Instagram/Facebook/TikTok)
    // expose window.webkit / window.webkit.messageHandlers as read-only native
    // objects. Stimulus controllers run as ES modules (strict mode), so writing
    // to a read-only property throws "Attempted to assign to readonly property".
    // Where the bridge is absent or writable we still install the noop
    // fallback; where it is read-only we leave the native bridge intact rather
    // than crashing.
    //
    // Only the handlers in NOOP_HANDLER_NAMES are defined — never a catch-all —
    // so probes for any other handler name stay undefined and third-party
    // scripts sniffing window.webkit.messageHandlers as an "in-app WebView"
    // signal are not misled on ordinary web sessions.
    try {
      window.webkit = window.webkit || {};
      const existingHandlers = window.webkit.messageHandlers || {};

      NOOP_HANDLER_NAMES.forEach((name) => {
        existingHandlers[name] = existingHandlers[name] || this.noopMessageHandler();
      });

      window.webkit.messageHandlers = existingHandlers;
    } catch {
      // Native bridge is read-only (real iOS WKWebView); leave it intact.
    }
  }

  ensureScriptLoaded() {
    return new Promise((resolve, reject) => {
      const existingScript = document.getElementById(SCRIPT_ID);
      if (existingScript) {
        if (existingScript.dataset.loaded === "true" || typeof window.maTrackEvent === "function") {
          resolve();
          return;
        }

        existingScript.addEventListener("load", () => resolve(), { once: true });
        existingScript.addEventListener("error", reject, { once: true });
        return;
      }

      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.async = true;
      script.type = "text/javascript";
      script.src = this.scriptUrlValue || DEFAULT_SCRIPT_URL;
      script.dataset.pid = this.pixelIdValue;
      script.dataset.key = this.keyValue;
      script.dataset.env = this.environmentValue;
      script.dataset.session = this.sessionValue;

      script.addEventListener("load", () => {
        script.dataset.loaded = "true";
        resolve();
      }, { once: true });
      script.addEventListener("error", reject, { once: true });

      if (this.element.parentNode) {
        this.element.parentNode.insertBefore(script, this.element);
      } else {
        document.head.appendChild(script);
      }
    });
  }

  trackPageView() {
    if (window._maPixelPageViewFired) {
      return;
    }

    window.maq(this.userIdValue, "page view");
    window._maPixelPageViewFired = true;
  }

  noopMessageHandler() {
    return {
      postMessage() {},
    };
  }
}
